/**
 * Contracts for module "notifications". Owner: ui-shell.
 * ONE notification service contract (QM-D14-003) used by audit, billing, approvals, integrations, leads:
 * producers send a SYSTEM_QUEUE message (or POST /v1/notifications/internal/events as `system`)
 * `{ type: 'notifications.<category>.<event>', tenantId, idempotencyKey, payload: NotificationPayload }`.
 * The service fans out to recipients, applies per-channel preferences (QM-D14-001) and the daily digest
 * (QM-D14-002), and sends email through the Microsoft Graph mail adapter with tracked delivery status.
 */
import { z } from 'zod';

export const NOTIFICATION_CATEGORIES = ['approvals', 'deliverables', 'leads', 'billing', 'security', 'audit', 'integrations', 'reports', 'system'] as const;
export const NotificationCategorySchema = z.enum(NOTIFICATION_CATEGORIES);
export type NotificationCategory = z.infer<typeof NotificationCategorySchema>;
export const CHANNELS = ['in_app', 'email'] as const;
export const ChannelSchema = z.enum(CHANNELS);
export type Channel = z.infer<typeof ChannelSchema>;

/** Categories that are always delivered on every channel regardless of preferences. */
export const MANDATORY_CATEGORIES: readonly NotificationCategory[] = ['security'];

/** Keys that must never travel in a notification payload (secrets, one-time tokens). */
export const FORBIDDEN_PAYLOAD_KEYS = /(token|secret|password|passcode|otp|api[_-]?key|authorization|cookie|signature|sig)$/i;

/** Portal-relative deep link (QM-D19-002). Absolute URLs and query strings are refused: no secrets in links. */
export const DeepLinkSchema = z.string().max(200).regex(/^\/[A-Za-z0-9/_-]*$/, 'deep links are portal-relative paths without query strings');

const ParamValue = z.union([z.string().max(500), z.number(), z.boolean()]);

export const NotificationPayloadSchema = z.object({
  messageKey: z.string().min(3).max(120).regex(/^[a-z0-9-]+(\.[A-Za-z0-9_-]+)+$/),
  params: z.record(z.string(), ParamValue).default({}),
  recipientRoles: z.array(z.string()).max(10).optional(),
  recipientUserIds: z.array(z.string()).max(200).optional(),
  link: DeepLinkSchema.optional(),
  /** force delivery on every channel (security notices) */
  mandatory: z.boolean().optional(),
  /** optional template for email rendering, defaults to messageKey */
  template: z.string().max(80).optional(),
}).passthrough();
export type NotificationPayload = z.infer<typeof NotificationPayloadSchema>;

export const NotificationEventSchema = z.object({
  type: z.string().regex(/^notifications\.[a-z-]+\.[a-z0-9_.-]+$/),
  tenantId: z.string().min(1),
  idempotencyKey: z.string().min(1).max(200),
  payload: NotificationPayloadSchema,
});
export type NotificationEvent = z.infer<typeof NotificationEventSchema>;

/** category from the event type, e.g. notifications.approval.request -> approvals */
export function categoryOf(type: string): NotificationCategory {
  const seg = type.split('.')[1] ?? 'system';
  const map: Record<string, NotificationCategory> = {
    approval: 'approvals', approvals: 'approvals', deliverable: 'deliverables', deliverables: 'deliverables',
    lead: 'leads', leads: 'leads', billing: 'billing', invoice: 'billing', security: 'security', audit: 'audit',
    integration: 'integrations', integrations: 'integrations', report: 'reports', reports: 'reports',
  };
  return map[seg] ?? 'system';
}

export const NotificationSchema = z.object({
  id: z.string(),
  category: NotificationCategorySchema,
  messageKey: z.string(),
  params: z.record(z.string(), ParamValue),
  link: z.string().nullable(),
  mandatory: z.boolean(),
  readAt: z.string().nullable(),
  createdAt: z.string(),
});
export type Notification = z.infer<typeof NotificationSchema>;

export const PreferenceMatrixSchema = z.partialRecord(NotificationCategorySchema, z.object({ in_app: z.boolean(), email: z.boolean() }).partial());
export const UpdatePreferencesSchema = z.object({
  channels: PreferenceMatrixSchema.optional(),
  digest: z.object({
    enabled: z.boolean(),
    /** local wall-clock HH:MM */
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    timezone: z.string().min(1).max(64),
  }).partial().optional(),
}).strict();
export type UpdatePreferences = z.infer<typeof UpdatePreferencesSchema>;

export const PreferencesSchema = z.object({
  channels: z.record(NotificationCategorySchema, z.object({ in_app: z.boolean(), email: z.boolean() })),
  mandatory: z.array(NotificationCategorySchema),
  digest: z.object({ enabled: z.boolean(), time: z.string(), timezone: z.string() }),
});
export type Preferences = z.infer<typeof PreferencesSchema>;

export const DeliveryStatusSchema = z.enum(['queued', 'sent', 'retrying', 'failed', 'skipped']);
export type DeliveryStatus = z.infer<typeof DeliveryStatusSchema>;

/** Direct send request (audit report email etc.) - same contract, one send per idempotency key. */
export const SendEmailRequestSchema = z.object({
  tenantId: z.string().min(1),
  idempotencyKey: z.string().min(1).max(200),
  to: z.array(z.string().email()).min(1).max(20),
  template: z.string().min(3).max(80),
  locale: z.enum(['en', 'ar']).default('en'),
  params: z.record(z.string(), ParamValue).default({}),
  link: DeepLinkSchema.optional(),
  category: NotificationCategorySchema.default('system'),
}).strict();
export type SendEmailRequest = z.infer<typeof SendEmailRequestSchema>;

export const MAX_SEND_RETRIES = 3;

// ---------- relay: producer events that are not yet in the notifications.* shape ----------
/**
 * Producers publish their own event types (MESSAGING_QUEUE / SYSTEM_QUEUE / CRM outbox). qm-jobs (or the
 * producer) forwards them unchanged to POST /v1/notifications/internal/relay, which maps them onto the one
 * notification contract with `relayToNotificationEvent`. Recipients: `payload.recipientRoles`,
 * `payload.recipients` ('role:<role>' or user ids), `payload.recipientUserIds` or `recipientUserId`.
 */
export const RELAYED_EVENT_PREFIXES: Readonly<Record<string, NotificationCategory>> = {
  'notifications.': 'system', // already in contract shape; category from the type
  'deliverables.': 'deliverables',
  'production.': 'deliverables',
  'reports.': 'reports',
  'social.': 'system',
  'billing.': 'billing',
  'crm.lead.': 'leads',
  'leadgen.': 'leads',
  'connectors.': 'integrations',
  'integrations.': 'integrations',
  'approvals.': 'approvals',
};

export const RelayMessageSchema = z.object({
  type: z.string().min(3).max(120).regex(/^[a-z][a-z0-9-]*(\.[a-z0-9_-]+)+$/),
  tenantId: z.string().min(1),
  idempotencyKey: z.string().min(1).max(200),
  recipientUserId: z.string().optional(),
  payload: z.record(z.string(), z.unknown()),
});
export type RelayMessage = z.infer<typeof RelayMessageSchema>;

const CATEGORY_SEGMENT: Record<NotificationCategory, string> = {
  approvals: 'approval', deliverables: 'deliverable', leads: 'lead', billing: 'billing', security: 'security',
  audit: 'audit', integrations: 'integration', reports: 'report', system: 'system',
};
/** id field in the payload -> portal deep link (portal-relative, no query string). */
const LINK_FIELDS: readonly [string, string][] = [
  ['itemId', '/approvals/'], ['approvalId', '/approvals/'], ['deliverableId', '/content/'], ['reportId', '/reports/'],
  ['leadId', '/leads/'], ['invoiceId', '/billing/invoices/'], ['conversationId', '/inbox/'], ['postId', '/calendar/'],
];
const SAFE_ID = /^[A-Za-z0-9_-]{1,64}$/;

export function relayToNotificationEvent(raw: unknown): NotificationEvent | null {
  const m = RelayMessageSchema.parse(raw);
  const prefix = Object.keys(RELAYED_EVENT_PREFIXES).find((p) => m.type.startsWith(p));
  if (!prefix) return null;
  const p = m.payload;
  const messageKey = typeof p.messageKey === 'string' ? p.messageKey : null;
  if (!messageKey) return null;
  const category = prefix === 'notifications.' ? categoryOf(m.type) : RELAYED_EVENT_PREFIXES[prefix]!;
  const roles = new Set<string>(Array.isArray(p.recipientRoles) ? p.recipientRoles.filter((x): x is string => typeof x === 'string') : []);
  const users = new Set<string>(Array.isArray(p.recipientUserIds) ? p.recipientUserIds.filter((x): x is string => typeof x === 'string') : []);
  if (Array.isArray(p.recipients)) {
    for (const r of p.recipients) {
      if (typeof r !== 'string') continue;
      if (r.startsWith('role:')) roles.add(r.slice(5));
      else users.add(r);
    }
  }
  if (m.recipientUserId) users.add(m.recipientUserId);
  const params: Record<string, string | number | boolean> = {};
  const baseParams = (p.params && typeof p.params === 'object' ? p.params : {}) as Record<string, unknown>;
  for (const [k, v] of Object.entries({ ...p, ...baseParams })) {
    if (['messageKey', 'recipients', 'recipientRoles', 'recipientUserIds', 'params', 'link', 'mandatory', 'template'].includes(k)) continue;
    if (FORBIDDEN_PAYLOAD_KEYS.test(k)) continue;
    if (typeof v === 'number' || typeof v === 'boolean') params[k] = v;
    else if (typeof v === 'string' && v.length <= 500 && !/^https?:\/\//i.test(v)) params[k] = v;
  }
  let link = typeof p.link === 'string' && DeepLinkSchema.safeParse(p.link).success ? p.link : undefined;
  if (!link) {
    for (const [field, base] of LINK_FIELDS) {
      const v = p[field];
      if (typeof v === 'string' && SAFE_ID.test(v)) { link = `${base}${v}`; break; }
    }
  }
  const type = prefix === 'notifications.' ? m.type : `notifications.${CATEGORY_SEGMENT[category]}.${m.type.replace(/\./g, '_')}`;
  return NotificationEventSchema.parse({
    type,
    tenantId: m.tenantId,
    idempotencyKey: m.idempotencyKey,
    payload: {
      messageKey, params,
      ...(roles.size ? { recipientRoles: [...roles] } : {}),
      ...(users.size ? { recipientUserIds: [...users] } : {}),
      ...(link ? { link } : {}),
      ...(p.mandatory === true || category === 'security' ? { mandatory: true } : {}),
    },
  });
}

// ---------- system send to a recipient KIND (qm-audit and other Workers over the API service binding) ----------
/**
 * The caller names WHO, never (except `address`) an email address: qm-api resolves the verified address server side.
 *  - tenant_user_verified   the named active user of the tenant
 *  - tenant_owner_verified  every active customer_owner of the tenant
 *  - ops_oncall             the on-call ops mailbox (OPS_ALERT_EMAIL)
 *  - address                an explicit address the calling system owns the proof for: the address being verified
 *                           (intake_verification) or one verified through intake (intake_verified, anonymous buyers)
 */
export const NotificationRecipientSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('tenant_user_verified'), userId: z.string().min(1).max(64) }).strict(),
  z.object({ kind: z.literal('tenant_owner_verified') }).strict(),
  z.object({ kind: z.literal('ops_oncall') }).strict(),
  /** the Quixo Marketing support mailbox (SUPPORT_EMAIL, resolved by qm-api): website enquiries such as the scoping questionnaire */
  z.object({ kind: z.literal('support_inbox') }).strict(),
  z.object({ kind: z.literal('address'), address: z.string().email().max(254), basis: z.enum(['intake_verification', 'intake_verified']) }).strict(),
]);
export type NotificationRecipient = z.infer<typeof NotificationRecipientSchema>;

/** POST /v1/notifications/internal/send-recipient (system actor only). One delivery per idempotency key. */
export const SendToRecipientRequestSchema = z.object({
  tenantId: z.string().min(1).max(64).nullable(),
  template: z.string().min(3).max(80),
  recipient: NotificationRecipientSchema,
  locale: z.enum(['en', 'ar']).default('en'),
  /** IDs and display strings only */
  data: z.record(z.string(), z.string().max(2048)).default({}),
  /** pre-rendered, brand-compliant content; without it the template key is rendered by qm-api */
  content: z.object({ subject: z.string().min(1).max(300), html: z.string().min(1).max(200_000), text: z.string().max(100_000) }).strict().optional(),
  idempotencyKey: z.string().min(1).max(200),
}).strict();
export type SendToRecipientRequest = z.infer<typeof SendToRecipientRequestSchema>;
